import { View, Text, Image, Svg, Path, Defs, LinearGradient, Stop, Rect, StyleSheet } from "@react-pdf/renderer";
import type { PdfWidgetSnapshot } from "../pdfTypes";
import type { PdfRect } from "../pdfLayout";
import type { ProcessedWidgetData } from "../../Processing/types";
import {
  injuryName,
  formatActiveAge,
  injuryRangeParts,
  sortAvailabilityEntries,
} from "@components/DashboardAlt/Utils/playerCardFormat";

const ACTIVE_CAP = 4;
// Photo-area background — vertical linear gradient mirroring the dashboard's .pc-photo.
// react-pdf can't gradient a View background, so it's rendered as an SVG LinearGradient (below).
const PHOTO_GRAD_TOP = "#ffffff";
const PHOTO_GRAD_BOTTOM = "#e7eaee";
const SILHOUETTE_COLOR = "#8a99ab"; // matches .pc-silhouette fill
const SILHOUETTE_PX = 58; // matches .pc-silhouette width/height
// Same person-silhouette path the dashboard's PlayerCardView renders as the no-photo fallback.
const SILHOUETTE_PATH =
  "M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12Zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8Z";
const RANGE_COL_PX = 36; // .pc-range-date width (3.6em @ 10px)
// react-pdf's default line box is tighter than the browser's `line-height:
// normal` for -apple-system, which compresses the stacked rows and leaves a
// large bottom gap. This restores per-row height to match the on-screen card.
const LINE = 1.5;

type PlayerCardSnapshot = Extract<PdfWidgetSnapshot, { type: "player_card" }>;

/** Pure, synchronous snapshot builder — pre-formats every section via the
 *  existing playerCardFormat utils so the renderer stays presentation-only. */
export function buildPlayerCardSnapshot(
  processed: ProcessedWidgetData,
): PlayerCardSnapshot | null {
  if (processed.type !== "player_card") return null;
  const { data } = processed;

  const subline = [
    data.position !== undefined ? data.position : null,
    data.jersey !== undefined ? `#${data.jersey}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const stats: { label: string; value: string }[] = [];
  if (data.top_speed !== undefined) stats.push({ label: "Top Speed", value: String(data.top_speed) });
  if (data.bodyweight_baseline !== undefined)
    stats.push({ label: "Body weight Baseline", value: String(data.bodyweight_baseline) });

  const snapshot: PlayerCardSnapshot = {
    type: "player_card",
    stats,
  };

  // No legacy `data.headshot` — only the permissioned ref is rendered; useExportPdf
  // authed-fetches it into snapshot.headshot (data-URL), else silhouette.
  if (data.headshot_image_url) snapshot.headshot_image_url = data.headshot_image_url;
  if (data.name !== undefined) snapshot.name = data.name;
  if (subline) snapshot.subline = subline;

  if (data.last_5_status !== undefined) {
    snapshot.availability = sortAvailabilityEntries(data.last_5_status).map((c) => ({
      color: c.color,
      date: c.date,
    }));
  }
  if (data.active_injuries !== undefined) {
    snapshot.activeInjuries = {
      items: data.active_injuries.slice(0, ACTIVE_CAP).map((inj) => ({
        name: injuryName(inj),
        meta: formatActiveAge(inj),
      })),
      total: data.active_injuries.length,
    };
  }
  if (data.last_5_injuries !== undefined) {
    snapshot.recentInjuries = {
      items: data.last_5_injuries.map((inj) => {
        const { start, end } = injuryRangeParts(inj);
        return { name: injuryName(inj), start, end };
      }),
    };
  }

  return snapshot;
}

const s = StyleSheet.create({
  cell: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    borderRadius: 4,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  // Absolutely-positioned so overflow:hidden on `cell` actually clips
  // injury-heavy content at the tile boundary (react-pdf only clips
  // absolutely-positioned children — see textPdfRenderer).
  inner: { position: "absolute", top: 0, left: 0, right: 0 },
  photo: { backgroundColor: PHOTO_GRAD_TOP, alignItems: "center", justifyContent: "center", overflow: "hidden" },
  photoImg: { width: "100%", height: "100%", objectFit: "contain" },
  photoBg: { position: "absolute", top: 0, left: 0 },
  silhouette: { opacity: 0.5 },
  name: { fontWeight: 700, color: "#1f2733" },
  sub: { color: "#7a8696", lineHeight: LINE },
  body: { flexDirection: "column" },
  section: { backgroundColor: "#f7f9fb", borderWidth: 1, borderColor: "#f0f2f5" },
  secLabel: { textTransform: "uppercase", color: "#9aa6b4", fontWeight: 700, letterSpacing: 0.5, lineHeight: LINE },
  row: { flexDirection: "row", justifyContent: "space-between" },
  rowKey: { color: "#5b6675", lineHeight: LINE },
  rowValue: { fontWeight: 700, color: "#1f2733", lineHeight: LINE },
  rowName: { fontWeight: 600, color: "#1f2733", lineHeight: LINE },
  rowMeta: { color: "#7a8696", lineHeight: LINE },
  avail: { flexDirection: "row", alignItems: "center" },
  dot: { borderWidth: 1, borderColor: "#aab4c0" },
  range: { flexDirection: "row" },
  rangeDate: { color: "#7a8696", textAlign: "left", lineHeight: LINE },
  rangeSep: { color: "#7a8696", textAlign: "center", lineHeight: LINE },
  none: { color: "#3a9d5d", lineHeight: LINE },
  noneNeutral: { color: "#9aa6b4", lineHeight: LINE },
  more: { color: "#9aa6b4", lineHeight: LINE },
  emptyWrap: { flex: 1, alignItems: "center", justifyContent: "center" },
  empty: { color: "#b3bcc7" },
});

export function renderPlayerCard(snapshot: PlayerCardSnapshot, rect: PdfRect, scale: number, id: string) {
  const frame = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  const gradId = `pcPhotoGrad-${id}`; // unique per widget so multiple cards don't share one gradient def

  const isEmpty =
    !snapshot.headshot &&
    !snapshot.name &&
    !snapshot.subline &&
    snapshot.stats.length === 0 &&
    !snapshot.availability &&
    !snapshot.activeInjuries &&
    !snapshot.recentInjuries;

  if (isEmpty) {
    return (
      <View style={[s.cell, frame]}>
        <View style={s.emptyWrap}>
          <Text style={[s.empty, { fontSize: 12 * scale }]}>No data</Text>
        </View>
      </View>
    );
  }

  const photoHeight = 160 * scale;
  const dotSize = 8 * scale;
  const showIdentityText = Boolean(snapshot.name || snapshot.subline);

  return (
    <View style={[s.cell, frame]}>
      <View style={s.inner}>
        {/* 1. Headshot area — linear-gradient background (mirrors the dashboard's .pc-photo)
            with the headshot image, or the silhouette fallback, centered on top. */}
        <View style={[s.photo, { height: photoHeight }]}>
          <Svg style={s.photoBg} width={rect.width} height={photoHeight}>
            <Defs>
              {/* userSpaceOnUse + explicit coords → reliably vertical (top→bottom);
                  objectBoundingBox 0..1 renders horizontally in react-pdf. */}
              <LinearGradient id={gradId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={photoHeight}>
                <Stop offset="0" stopColor={PHOTO_GRAD_TOP} />
                <Stop offset="1" stopColor={PHOTO_GRAD_BOTTOM} />
              </LinearGradient>
            </Defs>
            <Rect x="0" y="0" width={rect.width} height={photoHeight} fill={`url(#${gradId})`} />
          </Svg>
          {snapshot.headshot ? (
            <Image src={snapshot.headshot} style={s.photoImg} />
          ) : (
            <Svg
              width={SILHOUETTE_PX * scale}
              height={SILHOUETTE_PX * scale}
              viewBox="0 0 24 24"
              style={s.silhouette}
            >
              <Path d={SILHOUETTE_PATH} fill={SILHOUETTE_COLOR} />
            </Svg>
          )}
        </View>

        {/* 2. Identity */}
        {showIdentityText && (
          <View style={{ paddingTop: 9 * scale, paddingHorizontal: 12 * scale }}>
            {snapshot.name && (
              <Text style={[s.name, { fontSize: 18 * scale, lineHeight: 1.18 }]}>{snapshot.name}</Text>
            )}
            {snapshot.subline && (
              <Text style={[s.sub, { fontSize: 12 * scale, marginTop: 2 * scale }]}>{snapshot.subline}</Text>
            )}
          </View>
        )}

        {/* Body */}
        <View
          style={[
            s.body,
            { paddingVertical: 10 * scale, paddingHorizontal: 11 * scale, gap: 5 * scale },
          ]}
        >
          {/* 3. Stats + 4. Availability (one panel — mirrors the live card's first pc-section) */}
          {(snapshot.stats.length > 0 || snapshot.availability) && (
            <View
              style={[
                s.section,
                { borderRadius: 9 * scale, paddingVertical: 8 * scale, paddingHorizontal: 10 * scale },
              ]}
            >
              {snapshot.stats.map((st, i) => (
                <View key={i} style={[s.row, { paddingVertical: 1 * scale }]}>
                  <Text style={[s.rowKey, { fontSize: 12 * scale }]}>{st.label}</Text>
                  <Text style={[s.rowValue, { fontSize: 12 * scale }]}>{st.value}</Text>
                </View>
              ))}
              {snapshot.availability && (
                <View style={[s.row, { paddingVertical: 1 * scale, alignItems: "center" }]}>
                  <Text style={[s.rowKey, { fontSize: 12 * scale }]}>Recent Avail.</Text>
                  <View style={[s.avail, { gap: 5 * scale }]}>
                    {snapshot.availability.map((a) => (
                      <View
                        key={a.date}
                        style={[
                          s.dot,
                          { width: dotSize, height: dotSize, borderRadius: dotSize / 2, backgroundColor: a.color },
                        ]}
                      />
                    ))}
                  </View>
                </View>
              )}
            </View>
          )}

          {/* 5. Active Injuries */}
          {snapshot.activeInjuries && (
            <View
              style={[
                s.section,
                { borderRadius: 9 * scale, paddingVertical: 8 * scale, paddingHorizontal: 10 * scale },
              ]}
            >
              <Text style={[s.secLabel, { fontSize: 9 * scale, marginBottom: 6 * scale }]}>Active Injuries</Text>
              {snapshot.activeInjuries.items.length === 0 ? (
                <Text style={[s.none, { fontSize: 12 * scale }]}>✓ No active injuries</Text>
              ) : (
                <>
                  {snapshot.activeInjuries.items.map((inj, i) => (
                    <View key={i} style={[s.row, { paddingVertical: 1 * scale }]}>
                      <Text style={[s.rowName, { fontSize: 12 * scale }]}>{inj.name}</Text>
                      <Text style={[s.rowMeta, { fontSize: 10 * scale }]}>{inj.meta}</Text>
                    </View>
                  ))}
                  {snapshot.activeInjuries.total > ACTIVE_CAP && (
                    <Text style={[s.more, { fontSize: 11 * scale }]}>
                      +{snapshot.activeInjuries.total - ACTIVE_CAP} more
                    </Text>
                  )}
                </>
              )}
            </View>
          )}

          {/* 6. Recent Injuries */}
          {snapshot.recentInjuries && (
            <View
              style={[
                s.section,
                { borderRadius: 9 * scale, paddingVertical: 8 * scale, paddingHorizontal: 10 * scale },
              ]}
            >
              <Text style={[s.secLabel, { fontSize: 9 * scale, marginBottom: 6 * scale }]}>Recent Injuries</Text>
              {snapshot.recentInjuries.items.length === 0 ? (
                <Text style={[s.noneNeutral, { fontSize: 12 * scale }]}>None recorded</Text>
              ) : (
                snapshot.recentInjuries.items.map((inj, i) => (
                  <View key={i} style={[s.row, { paddingVertical: 1 * scale }]}>
                    <Text style={[s.rowName, { fontSize: 12 * scale }]}>{inj.name}</Text>
                    <View style={[s.range, { gap: 4 * scale }]}>
                      <Text style={[s.rangeDate, { fontSize: 10 * scale, width: RANGE_COL_PX * scale }]}>
                        {inj.start}
                      </Text>
                      <Text style={[s.rangeSep, { fontSize: 10 * scale }]}>–</Text>
                      <Text style={[s.rangeDate, { fontSize: 10 * scale, width: RANGE_COL_PX * scale }]}>
                        {inj.end}
                      </Text>
                    </View>
                  </View>
                ))
              )}
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
